import { Amplify } from 'aws-amplify'
import { Hub } from 'aws-amplify/utils'
import {
  fetchAuthSession,
  signIn,
  signOut,
  getCurrentUser,
  AuthUser,
  confirmSignIn,
  resetPassword,
  confirmResetPassword,
  updatePassword
} from 'aws-amplify/auth'

import { defineStore } from 'pinia'
import { useAppStore } from '@/stores/app'
import { getUserData } from '@/api/user'

export enum AuthMode {
  SIGN_IN,
  CONFIRM_SIGN_IN,
  RESET_PASSWORD,
  CONFIRM_RESET_PASSWORD
}

type UserData = {
  username: string
  createdAt: Date
  updatedAt: Date
  groups: undefined | Array<string>
  discountPercentage: number | undefined
  chargePercentage: number | undefined
  isAdmin: boolean
  locale: string
  logo: null | string
  maintainer?: boolean
  market?: string
  hidePrice?: boolean
  b2cPrice?: boolean
}

type AuthState = {
  provider: object | null
  user: AuthUser | null
  userData: UserData | null
  mode: AuthMode
  jwt: string | null
  isAuthenticating: boolean
  error: string | null
  resetPasswordUsername: string
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    provider: null,
    user: null,
    userData: null,
    mode: AuthMode.SIGN_IN,
    jwt: null,
    isAuthenticating: false,
    error: null,
    resetPasswordUsername: ''
  }),
  getters: {
    isAuthenticated: state => Boolean(state.user && Object.keys(state.user).length > 0),
    initials: state => {
      const name = state.user?.username || ''
      const nameParts = name ? name.split('.') : []
      if (nameParts.length > 1) return `${nameParts[0][0]}${nameParts[1][0]}`
      if (nameParts.length === 1) return `${nameParts[0][0]}${nameParts[0][1]}`
      return '-'
    },
    isAdmin: state => state.userData?.isAdmin || false,
    isMaintainer: state => state.userData && state.userData.maintainer
  },
  actions: {
    initProvider() {
      const appStore = useAppStore()
      const authSettings = appStore.settings?.auth
      Amplify.configure({
        Auth: {
          Cognito: {
            userPoolClientId: authSettings?.userPoolWebClientId as string,
            userPoolId: authSettings?.userPoolId as string
          }
        }
      })
      Hub.listen('auth', (data: { payload: { event: string } }) => {
        switch (data.payload.event) {
          case 'signedOut':
            window.location.reload()
            break
        }
      })
    },
    clear() {
      this.error = null
    },
    async login(username: string, password: string) {
      this.error = ''
      this.isAuthenticating = true
      try {
        const result = await signIn({
          username,
          password,
          options: { authFlowType: 'USER_PASSWORD_AUTH' }
        })
        if (result.isSignedIn === true && result.nextStep.signInStep === 'DONE') {
          this.error = ''
          return true
        }
        if (
          result.isSignedIn === false &&
          result.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED'
        ) {
          this.mode = AuthMode.CONFIRM_SIGN_IN
        }

        return false
      } catch (e) {
        if (e instanceof Error) {
          this.error = e.message
        } else {
          this.error = 'GENERIC_ERROR'
        }
      } finally {
        this.isAuthenticating = false
      }
    },
    async confirmLogin(password: string) {
      this.error = ''
      this.isAuthenticating = true
      try {
        const result = await confirmSignIn({ challengeResponse: password })
        if (result.isSignedIn === true && result.nextStep.signInStep === 'DONE') {
          this.error = ''
          return true
        }
        return false
      } catch (e) {
        if (e instanceof Error) {
          this.error = e.message
        } else {
          this.error = 'GENERIC_ERROR'
        }
      } finally {
        this.isAuthenticating = false
      }
    },
    async resetPassword(username: string) {
      this.error = ''
      this.isAuthenticating = true
      try {
        const result = await resetPassword({ username: username })

        if (result.nextStep.resetPasswordStep === 'CONFIRM_RESET_PASSWORD_WITH_CODE') {
          this.resetPasswordUsername = username
          this.mode = AuthMode.CONFIRM_RESET_PASSWORD
        }
        return false
      } catch (e) {
        if (e instanceof Error) {
          this.error = e.message
        } else {
          this.error = 'GENERIC_ERROR'
        }
      } finally {
        this.isAuthenticating = false
      }
    },
    async confirmResetPassword(code: string, password: string) {
      try {
        await confirmResetPassword({
          username: this.resetPasswordUsername,
          confirmationCode: code,
          newPassword: password
        })
        this.error = ''
        this.mode = AuthMode.SIGN_IN
        return true
      } catch (e) {
        if (e instanceof Error) {
          if (e.message.includes('Attempt limit exceeded')) {
            this.error = 'LIMIT_EXEEDED_ERROR'
          } else if (e.message.includes('Incorrect username or password')) {
            this.error = 'WRONG_PASSWORD'
          } else if (e.message.includes('Invalid verification code provided')) {
            this.error = 'INVALID_CODE'
          } else {
            this.error = 'INVALID_PASSWORD'
          }
        } else {
          this.error = 'GENERIC_ERROR'
        }
      } finally {
        this.isAuthenticating = false
      }
    },
    async updatePassword(oldPwd: string, newPwd: string) {
      this.error = ''
      this.isAuthenticating = true
      try {
        await updatePassword({
          oldPassword: oldPwd,
          newPassword: newPwd
        })
        this.error = ''
        return true
      } catch (e) {
        if (e instanceof Error) {
          if (e.message.includes('Attempt limit exceeded')) {
            this.error = 'LIMIT_EXEEDED_ERROR'
          } else if (e.message.includes('Incorrect username or password')) {
            this.error = 'WRONG_PASSWORD'
          } else {
            this.error = 'INVALID_PASSWORD'
          }
        } else {
          this.error = 'GENERIC_ERROR'
        }
      } finally {
        this.isAuthenticating = false
      }
    },
    async checkUser() {
      try {
        this.user = await getCurrentUser()
        if (this.isAuthenticated) {
          if (this.userData === null) {
            const appStore = useAppStore()
            appStore.setIsFetching(true)
            this.userData = await getUserData()
            if (this.userData?.locale) {
              appStore.defaultLanguage = this.userData.locale
            }
            appStore.setIsFetching(false)
          }
        }
      } catch (e) {
        this.user = null
      }
    },
    async refreshToken() {
      const session = await fetchAuthSession({ forceRefresh: true })
      const token = session.tokens?.idToken?.toString()
      this.jwt = token as string
    },
    async logout() {
      const appStore = useAppStore()
      appStore.setIsFetching(true)
      await signOut()
    }
  }
})
